import { useTranslation } from '@/i18n/useTranslation';

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuditLog } from '@/hooks/useAdmin';
import { TableSkeleton } from '@/components/common/LoadingSkeleton';
import { InlineError } from '@/components/common/InlineError';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';

const PAGE_SIZE = 25;

export function AuditLogPage() {
  const { displayValue, locale, t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState(searchParams.get('entity_type') ?? '');
  const [action, setAction] = useState(searchParams.get('action') ?? '');

  const auditLog = useAuditLog({
    page,
    page_size: PAGE_SIZE,
    entity_type: entityType || undefined,
    action: action || undefined,
  });

  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <header>
        <h1>{t('Admin · Audit Log')}</h1>
        <p>{t('API changes and database backup requests and outcomes, in chronological order.')}</p>
      </header>

      <div className="glass-panel cluster">
        <label className="field">
          <span className="field__label">{t('Entity type')}</span>
          <input
            type="text"
            className="input"
            placeholder={t('e.g. inspection, order, user')}
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('Action')}</span>
          <select
            className="select"
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
          >
            <option value="">{t('Any action')}</option>
            <option value="create">{t('Create')}</option>
            <option value="update">{t('Update')}</option>
            <option value="delete">{t('Delete')}</option>
            <option value="admin.backup.request">{t('Backup requested')}</option>
            <option value="admin.backup.completed">{t('Backup completed')}</option>
            <option value="admin.backup.failed">{t('Backup failed')}</option>
          </select>
        </label>
      </div>

      <section className="glass-panel">
        {auditLog.isLoading && <TableSkeleton rows={PAGE_SIZE} cols={6} />}
        {auditLog.isError && (
          <InlineError error={auditLog.error} onRetry={() => auditLog.refetch()} />
        )}
        {auditLog.data && auditLog.data.items.length === 0 && (
          <EmptyState title={t('No audit events found')} />
        )}
        {auditLog.data && auditLog.data.items.length > 0 && (
          <div className="stack">
            <div className="table-wrap">
              <table className="data-table">
                <caption className="visually-hidden">{t('Audit log')}</caption>
                <thead>
                  <tr>
                    <th>{t('Timestamp')}</th>
                    <th>{t('Actor')}</th>
                    <th>{t('Action')}</th>
                    <th>{t('Entity')}</th>
                    <th>{t('Entity ID')}</th>
                    <th>{t('Details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.data.items.map((entry) => (
                    <tr key={entry.id}>
                      <td className="font-mono">
                        {new Date(entry.occurred_at).toLocaleString(locale)}
                      </td>
                      <td className="font-mono">{entry.actor_user_id ?? t('system')}</td>
                      <td>{displayValue(entry.action)}</td>
                      <td>{displayValue(entry.entity_type)}</td>
                      <td className="font-mono">{entry.entity_id}</td>
                      <td>
                        {entry.entity_type === 'backup_job' ? (
                          <div className="stack">
                            {typeof entry.after?.trigger === 'string' && (
                              <span>{displayValue(entry.after.trigger)}</span>
                            )}
                            {typeof entry.after?.completed_at === 'string' && (
                              <span>
                                {t('Finished at')}:{' '}
                                {new Date(entry.after.completed_at).toLocaleString(locale)}
                              </span>
                            )}
                            {typeof entry.after?.file_size_bytes === 'number' && (
                              <span>
                                {t('{count} bytes', {
                                  count: entry.after.file_size_bytes.toLocaleString(locale),
                                })}
                              </span>
                            )}
                            {typeof entry.after?.error_message === 'string' && (
                              <span>{entry.after.error_message}</span>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={auditLog.data.total}
              onPageChange={setPage}
            />
          </div>
        )}
      </section>
    </div>
  );
}
